/**
 * EditorDrawerTab — Registers one "Muokkaus" tab on the right-edge
 * drawer rail. Inside, sub-tabs toggle between Version history and
 * Visible sections.
 *
 * Replaces the previous EditHistoryDrawerTab (single tab) and the
 * floating ReportSectionsAdmin panel (now lives as a sub-tab here).
 * Admin-only, like its predecessors.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Pencil, History, ListChecks } from 'lucide-react';
import { useDrawer } from '../DrawerSystem';
import { useReportEdits } from './ReportEditsContext';
import EditHistoryPanel from './EditHistoryPanel';
import SectionVisibilityPanel from './SectionVisibilityPanel';

const SubTabs = styled.div`
  display: flex;
  background: #0a0a1a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const SubTab = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? '#7c3aed' : 'transparent'};
  color: ${p => p.$active ? 'white' : 'rgba(255,255,255,0.5)'};
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 120ms, border-color 120ms;
  &:hover { color: white; }
`;

const PanelWrap = styled.div`
  height: calc(100% - 44px);
  overflow: hidden;
`;

function EditorPanel() {
  const [tab, setTab] = useState('history');
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SubTabs role="tablist">
        <SubTab
          type="button"
          role="tab"
          $active={tab === 'history'}
          aria-selected={tab === 'history'}
          onClick={() => setTab('history')}
        >
          <History size={14} /> Versiohistoria
        </SubTab>
        <SubTab
          type="button"
          role="tab"
          $active={tab === 'sections'}
          aria-selected={tab === 'sections'}
          onClick={() => setTab('sections')}
        >
          <ListChecks size={14} /> Osiot
        </SubTab>
      </SubTabs>
      <PanelWrap>
        {tab === 'history' ? <EditHistoryPanel /> : <SectionVisibilityPanel />}
      </PanelWrap>
    </div>
  );
}

const EditorDrawerTab = () => {
  const { registerTab, unregisterTab } = useDrawer();
  const { canEdit } = useReportEdits();

  useEffect(() => {
    if (!canEdit) return;

    registerTab({
      id: 'editor',
      label: 'Muokkaus',
      icon: <Pencil size={18} />,
      color: 'rgba(124, 58, 237, 0.3)',
      borderColor: 'rgba(124, 58, 237, 0.5)',
      order: 3,
      persistent: true,
      render: () => <EditorPanel />,
    });

    return () => unregisterTab('editor');
  }, [canEdit, registerTab, unregisterTab]);

  return null;
};

export default EditorDrawerTab;
